import type { ReactNode } from "react";

import { resolvePage, supportPages } from "../navigation.ts";
import type { Session } from "../session.ts";

/**
 * What this console can and cannot do yet, said out loud.
 *
 * Not a placeholder, and on this console not a small table either: `20 WP19 Support Console.dc.html`
 * draws six pages and **two of them have no operation a browser can call**. Each of those two
 * carries its own measured reason in `navigation.ts`, and this page is where an agent reads them
 * — rather than discovering, one rail item at a time, that a third of the suite is not there.
 *
 * It is the right page for them for the same reason the rail's hidden-page count excludes them: a
 * page blocked by the platform is not a page an agent can be given permission to see, so
 * telling them "ask whoever administers your roles" would send them to somebody who can do nothing.
 *
 * A stub pretending to be a macro library would teach an agent that the console is unreliable —
 * and on a console whose job is answering a customer, an agent who stops trusting a screen stops
 * using it and starts guessing. This teaches them something true instead.
 *
 * **Both of this console's two are blocked on a service rather than on a route**, which this page
 * says per row: the design cites the customer-360 read model for the macros and the escalation
 * service for the ratings, and neither holds what its page draws. A reason that said "internal
 * route" would send somebody to publish one.
 */

export interface PageOutlookProps {
  readonly session: Session;
}

export function PageOutlook({ session }: PageOutlookProps): ReactNode {
  const rows = supportPages.map((page) => ({
    page,
    resolved: resolvePage(page, session.scopes),
  }));
  const blocked = rows.filter(({ page }) => page.blockedBy !== undefined).length;

  return (
    <section className="page">
      <h1 className="page__title">Console readiness</h1>
      <p className="page__lead">
        {`This console draws ${String(rows.length)} pages and ${String(blocked)} of them have no operation behind them yet. Each row below says what is missing, measured against the published contracts rather than remembered.`}
      </p>

      <table className="outlook">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Your access</th>
            <th scope="col">Waiting on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ page, resolved }) => (
            <tr key={page.id}>
              <th scope="row">{page.title}</th>
              <td>
                {resolved.access === "forbidden"
                  ? page.blockedBy === undefined
                    ? "No permission"
                    : "Not available"
                  : resolved.access === "read_only"
                    ? "View only"
                    : "Full"}
              </td>
              <td>
                {page.blockedBy ??
                  "A screen. The operation and your permissions are both already in place."}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
